import { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { userApi } from '../../lib/api';
import { Card, Button, Input, Icon } from '../../components/ui';
import UserTable from '../../components/users/UserTable';
import UserInviteModal from '../../components/users/UserInviteModal';
import UserEditModal from '../../components/users/UserEditModal';
import DebugPanel from '../../components/users/DebugPanel';

interface User {
  id: string;
  email: string;
  role: string;
  name: string;
  created_at: string;
  last_sign_in_at: string | null;
  is_banned: boolean;
  agency_id: string;
  agencies?: {
    name: string;
  };
}

interface Agency {
  id: string;
  name: string;
}

export default function UsersPanel() {
  const [users, setUsers] = useState<User[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [userPermission, setUserPermission] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
    agency_id: '',
  });
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('authenticated');
  const [inviteAgencyId, setInviteAgencyId] = useState('');
  const [inviteStatus, setInviteStatus] = useState<{
    loading: boolean;
    success: boolean;
    error?: string;
    inviteLink?: string;
  }>({
    loading: false,
    success: false,
  });
  const [confirmAction, setConfirmAction] = useState<{
    open: boolean;
    userId: string;
    type: 'ban' | 'unban' | 'makeAdmin' | 'delete';
    email: string;
    isProcessing: boolean;
  }>({
    open: false,
    userId: '',
    type: 'ban',
    email: '',
    isProcessing: false,
  });

  const navigate = useNavigate();
  const { user } = useUser();
  const currentUserId = user?.id;

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate('/login');
        return;
      }

      // Get current user's role for debug panel
      const { data: currentUserData, error: currentUserError } = await supabase
        .from('users')
        .select('role')
        .eq('id', sessionData.session.user.id)
        .single();

      if (!currentUserError && currentUserData) {
        setUserPermission(currentUserData.role ?? '');
      }

      // Fetch agencies
      const { data: agenciesData, error: agenciesError } = await supabase
        .from('agencies')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (agenciesError) throw agenciesError;
      setAgencies(agenciesData || []);

      // Fetch users with agency relationship
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select(
          `
          *,
          agencies:agency_id (
            name
          )
        `
        )
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;
      // The users row keeps name/role nullable; this panel treats them as text.
      setUsers((usersData || []) as unknown as User[]);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users. ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const checkAdminStatus = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Get user role from the public users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;

      if (userData?.role !== 'admin') {
        setIsAdmin(false);
        setError('You need administrator privileges to access this page.');
        setIsLoading(false);
        return;
      }

      setIsAdmin(true);
      fetchUsers();
    } catch (error) {
      console.error('Error checking admin status:', error);
      setError('Failed to verify administrator privileges.');
      setIsLoading(false);
    }
  }, [navigate, fetchUsers]);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  const handleBanUser = async (userId: string, isBanned: boolean) => {
    try {
      setError(null);

      await userApi.updateUserBanStatus(userId, !isBanned);

      // Update the local users state
      setUsers((prev) => prev.map((user) => (user.id === userId ? { ...user, is_banned: !isBanned } : user)));
    } catch (error) {
      console.error('Error updating user ban status:', error);
      setError(
        'Failed to update user ban status: ' + (error instanceof Error ? error.message : String(error))
      );
    }
  };

  const handleMakeAdmin = async (userId: string) => {
    try {
      setError(null);

      await userApi.updateUserRole(userId, 'admin');

      // Update the local users state
      setUsers((prev) => prev.map((user) => (user.id === userId ? { ...user, role: 'admin' } : user)));
    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  // New function to handle user deletion
  const handleDeleteUser = async (userId: string) => {
    try {
      setError(null);

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate('/login');
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/user-management/users/${userId}`;

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      // Remove the deleted user from the local state
      setUsers((prev) => prev.filter((user) => user.id !== userId));

      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user: ' + (error instanceof Error ? error.message : String(error)));
      return false;
    }
  };

  const handleEditUser = (userToEdit: User) => {
    setSelectedUser(userToEdit);
    setEditForm({
      name: userToEdit.name || '',
      email: userToEdit.email,
      role: userToEdit.role,
      agency_id: userToEdit.agency_id,
    });
    setIsEditModalOpen(true);
  };

  const handleSaveUserChanges = async () => {
    if (!selectedUser) return;

    setIsEditSubmitting(true);
    setError(null);

    try {
      // Update the user in the database
      const { error: updateError } = await supabase
        .from('users')
        .update({
          name: editForm.name,
          role: editForm.role,
          agency_id: editForm.agency_id,
        })
        .eq('id', selectedUser.id);

      if (updateError) throw updateError;

      // Refresh users to get updated agency info
      await fetchUsers();

      // Close the modal
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleInviteUser = async () => {
    try {
      setInviteStatus({ loading: true, success: false });

      if (!inviteEmail.trim()) {
        setInviteStatus({
          loading: false,
          success: false,
          error: 'Email address is required',
        });
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate('/login');
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/user-management/users/invite`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          agency_id: inviteAgencyId,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to invite user');
      }

      // Success
      setInviteStatus({
        loading: false,
        success: true,
        inviteLink: responseData.inviteLink,
      });

      // Fetch updated users list
      fetchUsers();

      // Reset form after short delay
      setTimeout(() => {
        setInviteEmail('');
        setInviteRole('authenticated');
        setInviteAgencyId('');
      }, 2000);
    } catch (error) {
      console.error('Error inviting user:', error);
      setInviteStatus({
        loading: false,
        success: false,
        error: 'Failed to invite user: ' + (error instanceof Error ? error.message : String(error)),
      });
    }
  };

  const closeModal = () => {
    setInviteModalOpen(false);
    // Reset invite status
    setTimeout(() => {
      setInviteStatus({ loading: false, success: false });
    }, 300);
  };

  const handleConfirmAction = useCallback(async () => {
    try {
      setConfirmAction((prev) => ({ ...prev, isProcessing: true }));

      let success = false;

      if (confirmAction.type === 'ban') {
        await handleBanUser(confirmAction.userId, false);
        success = true;
      } else if (confirmAction.type === 'unban') {
        await handleBanUser(confirmAction.userId, true);
        success = true;
      } else if (confirmAction.type === 'makeAdmin') {
        await handleMakeAdmin(confirmAction.userId);
        success = true;
      } else if (confirmAction.type === 'delete') {
        success = (await handleDeleteUser(confirmAction.userId)) ?? false;
      }

      // Close the dialog if successful
      if (success) {
        setConfirmAction((prev) => ({
          ...prev,
          open: false,
          isProcessing: false,
        }));
      } else {
        setConfirmAction((prev) => ({
          ...prev,
          isProcessing: false,
        }));
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
      setConfirmAction((prev) => ({ ...prev, isProcessing: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmAction.type, confirmAction.userId]);

  // Add keyboard event listener for Enter key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && confirmAction.open && !confirmAction.isProcessing) {
        event.preventDefault();
        handleConfirmAction();
      }
    };

    if (confirmAction.open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [confirmAction.open, confirmAction.isProcessing, handleConfirmAction]);

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const activeSeats = users.filter((user) => !user.is_banned).length;

  if (!isAdmin && !isLoading) {
    return (
      <Card>
        <div className="flex flex-col items-center text-center" style={{ padding: 'var(--space-8) 0' }}>
          <Icon
            name="circle-alert"
            size={28}
            style={{ color: 'var(--danger-500)', marginBottom: 'var(--space-4)' }}
          />
          <h2
            style={{
              margin: '0 0 var(--space-2)',
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              color: 'var(--text-body)',
            }}
          >
            Access Denied
          </h2>
          <p
            style={{
              margin: '0 0 var(--space-5)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
              maxWidth: '420px',
              lineHeight: 1.6,
            }}
          >
            You need administrator privileges to access this page. Please contact your system
            administrator if you believe this is an error.
          </p>
          <Button onClick={() => navigate('/clients')}>Return to Clients</Button>
        </div>
      </Card>
    );
  }

  return (
    <>
      <DebugPanel userPermission={userPermission} />

      {showInfoBanner && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-info-soft)',
            border: '1px solid rgba(110, 159, 212, 0.25)',
            color: 'var(--status-info)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <Icon name="circle-alert" size={15} style={{ marginTop: '2px', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p
              style={{
                margin: '0 0 2px',
                fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              }}
            >
              Edge Functions Are Required
            </p>
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>
              User management operations (invites, role changes, bans) are handled by Supabase Edge
              Functions that use the admin API securely on the server-side.
            </p>
            <button
              className="pressable"
              onClick={() => setShowInfoBanner(false)}
              style={{
                marginTop: '6px',
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: 'inherit',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <Icon name="circle-alert" size={15} />
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            className="pressable"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-sm)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <Card
        eyebrow="Team"
        title={`${activeSeats} active seats`}
        actions={
          <>
            <div className="hidden sm:block" style={{ width: '180px' }}>
              <Input
                icon="search"
                fieldSize="sm"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button size="sm" icon="plus" onClick={() => setInviteModalOpen(true)}>
              Invite User
            </Button>
          </>
        }
      >
        {isLoading ? (
          <div className="flex flex-col items-center" style={{ padding: 'var(--space-8) 0' }}>
            <div
              className="animate-spin"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: 'var(--radius-pill)',
                border: '3px solid var(--border-hairline)',
                borderTopColor: 'var(--accent)',
                marginBottom: 'var(--space-3)',
              }}
            />
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center" style={{ padding: 'var(--space-8) 0' }}>
            <Icon
              name="inbox"
              size={24}
              style={{ color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }}
            />
            <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              {searchTerm ? 'No users match that search.' : 'No users yet. Start by inviting some.'}
            </p>
            {!searchTerm && (
              <Button size="sm" icon="plus" onClick={() => setInviteModalOpen(true)}>
                Invite User
              </Button>
            )}
          </div>
        ) : (
          <UserTable
            users={filteredUsers}
            currentUserId={currentUserId || ''}
            isLoading={isLoading}
            searchTerm={searchTerm}
            onEditUser={handleEditUser}
            onBanStatusChange={handleBanUser}
            onRoleChange={handleMakeAdmin}
            onError={setError}
          />
        )}
      </Card>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmAction.open}
        onClose={() => setConfirmAction((prev) => ({ ...prev, open: false }))}
        className="relative z-50"
      >
        <div className="fixed inset-0" style={{ background: 'var(--surface-overlay)' }} aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad">
          <Dialog.Panel
            className="mx-auto w-full ck-menu-pop"
            style={{
              maxWidth: '400px',
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-pop)',
              padding: 'var(--space-6)',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
              Team
            </div>
            <Dialog.Title
              style={{
                margin: 0,
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--text-body)',
              }}
            >
              Confirm Action
            </Dialog.Title>
            <p
              style={{
                margin: 'var(--space-3) 0 var(--space-5)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to {confirmAction.type} user "{confirmAction.email}"?
              {confirmAction.type === 'delete' && ' This action cannot be undone.'}
            </p>

            <div className="flex justify-end" style={{ gap: '10px' }}>
              <Button
                variant="ghost"
                disabled={confirmAction.isProcessing}
                onClick={() => setConfirmAction((prev) => ({ ...prev, open: false }))}
              >
                Cancel
              </Button>
              <Button variant="danger" disabled={confirmAction.isProcessing} onClick={handleConfirmAction}>
                {confirmAction.isProcessing ? 'Processing...' : 'Confirm'}
              </Button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <UserEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        selectedUser={selectedUser}
        onSave={handleSaveUserChanges}
        editForm={editForm}
        setEditForm={setEditForm}
        isSubmitting={isEditSubmitting}
        agencies={agencies}
      />

      {/* Invite User Panel */}
      <UserInviteModal
        isOpen={inviteModalOpen}
        onClose={closeModal}
        onInviteUser={handleInviteUser}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteRole={inviteRole}
        setInviteRole={setInviteRole}
        inviteAgencyId={inviteAgencyId}
        setInviteAgencyId={setInviteAgencyId}
        inviteStatus={inviteStatus}
        agencies={agencies}
      />
    </>
  );
}
